/* ===========================================================
   lib/idb.js — a small promise wrapper around IndexedDB

   One database per account on this device ("dp-<user id>"), so two
   people sharing a computer never see each other's cached docs.
   =========================================================== */

const VERSION = 1;

function upgrade(db) {
  if (!db.objectStoreNames.contains('docs')) db.createObjectStore('docs', { keyPath: 'id' });
  if (!db.objectStoreNames.contains('entries')) {
    const s = db.createObjectStore('entries', { keyPath: 'k', autoIncrement: true });
    s.createIndex('doc', 'doc');
  }
  if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta', { keyPath: 'doc' });
  if (!db.objectStoreNames.contains('outbox')) db.createObjectStore('outbox', { keyPath: 'k', autoIncrement: true });
  if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv', { keyPath: 'k' });
  if (!db.objectStoreNames.contains('files')) db.createObjectStore('files', { keyPath: 'key' });
  if (!db.objectStoreNames.contains('conflicts')) db.createObjectStore('conflicts', { keyPath: 'doc' });
}

export function openDB(name) {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(name, VERSION);
    req.onupgradeneeded = () => upgrade(req.result);
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => db.close();
      resolve(db);
    };
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('The app is open in another tab that needs to be refreshed.'));
  });
}

export function deleteDB(name) {
  return new Promise(resolve => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = req.onerror = req.onblocked = () => resolve();
  });
}

const done = req => new Promise((resolve, reject) => {
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

/** Run fn(stores) in one transaction; resolves when it commits. */
export function tx(db, names, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(names, mode);
    const stores = Object.fromEntries((Array.isArray(names) ? names : [names]).map(n => [n, t.objectStore(n)]));
    let result;
    Promise.resolve(fn(stores)).then(r => { result = r; }, reject);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error || new Error('Saving on this device was interrupted.'));
  });
}

export const get = (store, key) => done(store.get(key));
export const getAll = (store, query) => done(store.getAll(query));
export const put = (store, value) => done(store.put(value));
export const add = (store, value) => done(store.add(value));
export const del = (store, key) => done(store.delete(key));
export const count = store => done(store.count());
